import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

interface AdMetrics {
  impressions: number;
  clicks: number;
  revenue: number;
  ctr: number;
}

interface AdConfig {
  enabled: boolean;
  frequency: 'low' | 'medium' | 'high';
  testMode: boolean;
  propellerZoneId: string;
  secondaryZoneId?: string; // Optional: for secondary ad placement (e.g., In-Page Push)
}

interface AdContextType {
  adConfig: AdConfig;
  updateAdConfig: (config: Partial<AdConfig>) => void;
  shouldShowAd: () => boolean;
  trackAdImpression: (zoneId: string) => void;
  trackAdClick: (zoneId: string) => void;
  adMetrics: Record<string, AdMetrics>;
  updateAdMetrics: (zoneId: string, metrics: Partial<AdMetrics>) => void;
}

const AdContext = createContext<AdContextType | undefined>(undefined);

export const useAd = () => {
  const context = useContext(AdContext);
  if (!context) {
    throw new Error('useAd must be used within an AdProvider');
  }
  return context;
};

interface AdProviderProps {
  children: ReactNode;
}

export const AdProvider: React.FC<AdProviderProps> = ({ children }) => {
  // Recommended: Use Native Banner (Interstitial) as primary (9532056),
  // and optionally In-Page Push (9532055) as secondary for minimal interference and good revenue.
  const [adConfig, setAdConfig] = useState<AdConfig>({
    enabled: true,
    frequency: 'low',
    testMode: false,
    propellerZoneId: '9532056', // Native Banner (Interstitial) - best balance
    secondaryZoneId: '9532055'  // In-Page Push - optional, low interference
  });

  const [adMetrics, setAdMetrics] = useState<Record<string, AdMetrics>>({});

  // Load config from localStorage
  useEffect(() => {
    const savedConfig = localStorage.getItem('adConfig');
    if (savedConfig) {
      setAdConfig(JSON.parse(savedConfig));
    }
  }, []);

  // Save config to localStorage
  useEffect(() => {
    localStorage.setItem('adConfig', JSON.stringify(adConfig));
  }, [adConfig]);

  // Load metrics from localStorage
  useEffect(() => {
    const savedMetrics = localStorage.getItem('adMetrics');
    if (savedMetrics) {
      setAdMetrics(JSON.parse(savedMetrics));
    }
  }, []);

  // Save metrics to localStorage
  useEffect(() => {
    localStorage.setItem('adMetrics', JSON.stringify(adMetrics));
  }, [adMetrics]);

  const updateAdConfig = (config: Partial<AdConfig>) => {
    setAdConfig(prev => ({ ...prev, ...config }));
  };

  const shouldShowAd = (): boolean => {
    if (!adConfig.enabled) return false;
    
    // Simple frequency control
    const lastAdTime = localStorage.getItem('lastAdTime');
    const now = Date.now();
    const timeSinceLastAd = lastAdTime ? now - parseInt(lastAdTime) : Infinity;
    
    const minInterval = {
      low: 300000, // 5 minutes
      medium: 180000, // 3 minutes
      high: 60000 // 1 minute
    }[adConfig.frequency];
    
    return timeSinceLastAd >= minInterval;
  };

  const trackAdImpression = (zoneId: string) => {
    setAdMetrics(prev => {
      const current = prev[zoneId] || { impressions: 0, clicks: 0, revenue: 0, ctr: 0 };
      const updated = {
        ...current,
        impressions: current.impressions + 1
      };
      updated.ctr = updated.impressions > 0 ? (updated.clicks / updated.impressions) * 100 : 0;
      
      return {
        ...prev,
        [zoneId]: updated
      };
    });
    
    localStorage.setItem('lastAdTime', Date.now().toString());
  };

  const trackAdClick = (zoneId: string) => {
    setAdMetrics(prev => {
      const current = prev[zoneId] || { impressions: 0, clicks: 0, revenue: 0, ctr: 0 };
      const updated = {
        ...current,
        clicks: current.clicks + 1,
        revenue: current.revenue + 0.01 // Simulate $0.01 per click
      };
      updated.ctr = updated.impressions > 0 ? (updated.clicks / updated.impressions) * 100 : 0;
      
      return {
        ...prev,
        [zoneId]: updated
      };
    });
  };

  const updateAdMetrics = (zoneId: string, metrics: Partial<AdMetrics>) => {
    setAdMetrics(prev => {
      const current = prev[zoneId] || { impressions: 0, clicks: 0, revenue: 0, ctr: 0 };
      const updated = { ...current, ...metrics };
      updated.ctr = updated.impressions > 0 ? (updated.clicks / updated.impressions) * 100 : 0;
      
      return {
        ...prev,
        [zoneId]: updated
      };
    });
  };

  const value: AdContextType = {
    adConfig,
    updateAdConfig,
    shouldShowAd,
    trackAdImpression,
    trackAdClick,
    adMetrics,
    updateAdMetrics
  };

  return (
    <AdContext.Provider value={value}>
      {children}
    </AdContext.Provider>
  );
}; 